import { ChurchCenterCustomFieldApiRecordWithRelationships } from "source/myChurchCenter/Profile/types"
import { FieldDataApiRecord } from "source/publishing/sermons/shared/types"

export function groupCustomFieldRecordsForSaving(
  state: ChurchCenterCustomFieldApiRecordWithRelationships[],
  records: ChurchCenterCustomFieldApiRecordWithRelationships[],
  uploadedFiles: { fileID: string; originalFileName: string }[],
) {
  type CustomFieldInStateAndPropsType = {
    recordInState: ChurchCenterCustomFieldApiRecordWithRelationships
    recordInProps: ChurchCenterCustomFieldApiRecordWithRelationships | undefined
  }

  const customFieldsInStateAndInProps = state.reduce<
    CustomFieldInStateAndPropsType[]
  >((acc, recordInState) => {
    if (recordInState.type !== "ChurchCenterCustomField") return acc

    const recordInProps = records.find(
      (record) =>
        record.id === recordInState.id &&
        record.type === "ChurchCenterCustomField",
    )

    acc.push({ recordInState, recordInProps })

    return acc
  }, [])

  type GroupedFieldData = {
    customFieldsToCreate: FieldDataApiRecord[]
    customFieldsToUpdate: FieldDataApiRecord[]
    customFieldsToDelete: FieldDataApiRecord[]
  }

  const fixFileFieldData = (fieldData: FieldDataApiRecord) => {
    const uploadedFile = uploadedFiles.find(
      (file) => file.originalFileName === fieldData.attributes.value,
    )

    if (uploadedFile) {
      return {
        ...fieldData,
        attributes: {
          ...fieldData.attributes,
          value: uploadedFile.fileID,
        },
      }
    }

    return fieldData
  }

  const { customFieldsToCreate, customFieldsToUpdate, customFieldsToDelete } =
    customFieldsInStateAndInProps.reduce<GroupedFieldData>(
      (acc, { recordInState, recordInProps }) => {
        const {
          relationships: {
            field_data: { data: fieldDataInState } = { data: [] },
          },
        } = recordInState

        const fieldDataInProps = recordInProps?.relationships?.field_data?.data

        // checking for records that need to be updated or created
        fieldDataInState.forEach((fdInState) => {
          const fdInProps = fieldDataInProps?.find(
            (fdInProps) => fdInProps.id === fdInState.id,
          )

          if (!fdInProps) {
            acc.customFieldsToCreate.push(fdInState)
            return acc
          }

          const hasUpdatedState =
            fdInState.attributes.value !== fdInProps.attributes.value

          if (hasUpdatedState && fdInState.attributes.value === "") {
            acc.customFieldsToDelete.push(fdInProps)
            return acc
          }

          if (hasUpdatedState) {
            acc.customFieldsToUpdate.push(fdInState)
            return acc
          }
        })

        // checking for records that need to be deleted
        fieldDataInProps?.forEach((fdInProps) => {
          const fdInState = fieldDataInState.find(
            (fdInState) => fdInState.id === fdInProps.id,
          )

          if (!fdInState) {
            acc.customFieldsToDelete.push(fdInProps)
          }
        })

        return acc
      },
      {
        customFieldsToCreate: [],
        customFieldsToUpdate: [],
        customFieldsToDelete: [],
      },
    )

  return {
    customFieldsToCreate: customFieldsToCreate.map(fixFileFieldData),
    customFieldsToUpdate: customFieldsToUpdate.map(fixFileFieldData),
    customFieldsToDelete,
  }
}
